// The entry screen's state and the API's entry (docs/app.md §9). The form keeps what was typed as text; toEntry turns it
// into the JSON the API builds the voucher from.
import type { BaseType, BillAlloc, Entry, EwayEntry, VLedger, VoucherReference } from "../lib/api";
import { parseAmount } from "../lib/money";
import { cleanEway, ewayProblem } from "./eway";

export const isInvoice = (b: BaseType) => b === "sales" || b === "purchase" || b === "credit_note" || b === "debit_note";
export const hasAccount = (b: BaseType) => b === "payment" || b === "receipt" || b === "contra";
export const lineSide = (b: BaseType): "Dr" | "Cr" | null =>
  ({ sales: "Cr", purchase: "Dr", credit_note: "Dr", debit_note: "Cr", payment: "Dr", receipt: "Cr", contra: "Dr" } as Record<string, "Dr" | "Cr">)[b] ?? null;

export const TITLES: Record<string, { account?: string; party?: string; lines: string }> = {
  sales: { party: "Customer", lines: "Revenue account" }, purchase: { party: "Vendor", lines: "Expense or purchase account" },
  credit_note: { party: "Customer", lines: "Returned / allowed" }, debit_note: { party: "Vendor", lines: "Returned / allowed" },
  payment: { account: "Paid from", lines: "Paid to" }, receipt: { account: "Received into", lines: "Received from" },
  contra: { account: "From", lines: "To" }, journal: { lines: "Particulars" },
};

export const isCashBank = (l: VLedger) => l.codes.includes("cash_in_hand") || l.codes.includes("bank_accounts") || l.codes.includes("bank_od");
export const isTaxLedger = (l: VLedger) => !!l.tax;

// payment terms on an invoice or bill (sql/250): the due date follows from the date
export const TERMS: [string, string, number | null][] = [["due_on_receipt", "Due on receipt", 0], ["net_7", "Net 7", 7], ["net_15", "Net 15", 15], ["net_30", "Net 30", 30],
  ["net_45", "Net 45", 45], ["net_60", "Net 60", 60], ["net_90", "Net 90", 90], ["eom", "Due end of the month", null], ["next_eom", "Due end of next month", null], ["custom", "Custom", null]];
export const TERMS_LABEL: Record<string, string> = Object.fromEntries(TERMS.map(([k, l]) => [k, l]));
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export function dueFor(terms: string, date: string): string | null {
  if (!date) return null;
  const d = new Date(date + "T00:00:00");
  const t = TERMS.find((x) => x[0] === terms);
  if (!t || terms === "custom") return null;
  if (t[2] != null) { d.setDate(d.getDate() + t[2]); return iso(d); }
  return iso(new Date(d.getFullYear(), d.getMonth() + (terms === "eom" ? 1 : 2), 0));
}
export const termsForDays = (days: number) => TERMS.find((t) => t[2] === days)?.[0] ?? "custom";

// GST rates: the slabs in force (5, 18, 28, 40 and nil), the special ones (gold and stones, rough diamonds), and 12%
// for supplies before 22 September 2025 (GST 2.0 moved most of 12% to 5%)
export const GST_RATES: [string, string][] = [["0", "0%"], ["5", "5%"], ["18", "18%"], ["28", "28%"], ["40", "40%"],
  ["12", "12% (before 22 Sep 2025)"], ["3", "3% (gold, silver, jewellery)"], ["0.25", "0.25% (rough precious stones)"], ["1.5", "1.5% (cut diamonds)"], ["0.1", "0.1% (merchant exports)"]];
// GST on a line: one choice for the rate and whether it is taxed at all
export const TAX_CHOICES: [string, string][] = [...GST_RATES.map(([k, l]) => [k, `GST ${l}`] as [string, string]),
  ["exempt", "Exempt"], ["nil_rated", "Nil-rated"], ["non_gst", "Non-GST supply"]];
export const taxChoice = (l: { rate: string; taxability?: string }) => (l.taxability && l.taxability !== "taxable" ? l.taxability : l.rate.trim() === "" ? "" : String(Number(l.rate)));
export const fromTaxChoice = (v: string): { rate: string; taxability: string } =>
  (["exempt", "nil_rated", "non_gst"].includes(v) ? { rate: "0", taxability: v } : { rate: v, taxability: "taxable" });

export interface ItemPart { variant_id: string | null; qty: string; uom_id: string | null; rate: string; batch: string; expiry: string }
export interface FormLine {
  key: number; ledger_id: string | null; amount: string; side: "Dr" | "Cr"; narration: string;
  hsn: string; rate: string; taxability?: string; bills?: BillAlloc[]; item?: ItemPart;   // taxability: from the ledger or item (exempt, nil_rated, non_gst)
  scheme?: string;                                                                   // a discount scheme applied (243): its id
  // the invoice form (sql/250): what the line is (an item, a product or service, or typed), quantity × rate less discount
  item_name: string; service_id: string | null; qty: string; price: string; unit: string; disc: string; discPct: boolean;
  cost?: CostShare[]; costPct?: boolean;   // 262_a: shared over cost centres, by amount or percentage
  person_id?: string | null;   // 269_a: the employee or student, on an account kept by person
  billable?: { customer_id: string; project_id?: string | null } | null;   // 263_c: a cost to bill on to a customer
  billable_ids?: string[];                                                 // 263_c: an invoice line billing those costs
}
export interface CostShare { cost_centre_id: string | null; value: string }
// a line's cost centre shares: what's wrong with them, or null (they must cover the line exactly)
export function costProblem(l: { cost?: CostShare[]; costPct?: boolean }, amount: number | null): string | null {
  const shares = (l.cost ?? []).filter((c) => c.cost_centre_id || c.value.trim());
  if (!shares.length) return null;
  if (shares.some((c) => !c.cost_centre_id)) return "choose a cost centre for each share";
  const vals = shares.map((c) => parseAmount(c.value));
  if (vals.some((v) => v == null || v <= 0)) return "give each cost centre a share above zero";
  const sum = Math.round(vals.reduce<number>((t, v) => t + (v ?? 0), 0) * 100) / 100;
  if (l.costPct) return sum === 100 ? null : `the cost centre shares add up to ${sum}%, not 100%`;
  return amount != null && Math.abs(sum - amount) > 0.004 ? `the cost centres add up to ${sum.toFixed(2)}, not the line's ${amount.toFixed(2)}` : null;
}
export interface TdsRow { party_ledger_id: string; provision_code: string; base: string; skip: boolean; skip_reason: string; gross?: boolean }
export interface SufferedRow { party_ledger_id: string; kind: "tds" | "tcs"; base: string; tax: string }
// an export or a supply to a SEZ (zero-rated, IGST Act s.16): under LUT / bond without IGST, or on payment of IGST
export interface ZeroRated { kind: "export" | "sez"; with_tax: boolean; igst_from_buyer: boolean | null; sb_no: string; sb_date: string; sb_port: string }
export const zeroSupplyType = (z: ZeroRated) => `${z.kind}_${z.with_tax ? "with_tax" : "lut"}`;
export function zeroOf(supplyType: string | null | undefined, ex?: { igst_from_buyer?: boolean | null; shipping_bill_no?: string | null; shipping_bill_date?: string | null; shipping_port?: string | null } | null): ZeroRated | null {
  const m = /^(export|sez)_(with_tax|lut)$/.exec(supplyType ?? "");
  return m ? { kind: m[1] as "export" | "sez", with_tax: m[2] === "with_tax", igst_from_buyer: ex?.igst_from_buyer ?? null,
               sb_no: ex?.shipping_bill_no ?? "", sb_date: ex?.shipping_bill_date ?? "", sb_port: ex?.shipping_port ?? "" } : null;
}

export interface Form {
  id?: string; type_id: string; base: BaseType; date: string; narration: string; reference_no: string; reference_date: string;
  account: string | null; party: string | null; party_bills?: BillAlloc[];
  // 258_a: GST on an advance received (receipts) and advances set against an invoice or bill
  advance_gst?: { rate: number } | null; adjust_advances?: { ref_name: string; amount: number }[];
  lines: FormLine[];
  gstOn: boolean; registration_id: string | null; place_of_supply: string | null; reverse_charge: boolean;
  original_voucher: string | null; note_unlinked: boolean;
  tds: TdsRow[]; suffered: SufferedRow[];
  itemsMode: boolean; warehouse: string | null;
  order_id?: string | null;
  ewaybill?: EwayEntry | null;
  eco_ledger?: string | null; eco_section?: "52" | "9_5";   // sold through an e-commerce operator (sql/123)
  eco_for?: { gstin: string } | null;                     // issued as the operator on a seller's behalf, s. 9(5) (sql/261_b)
  zero?: ZeroRated | null;                                // an export or SEZ supply: LUT or IGST paid, the shipping bill (sql/259_b)
  currency?: string; fx_rate?: string;                    // a foreign-currency entry (sql/019): "" = the base currency / the day's rate
  fields?: Record<string, string>;                        // the company's own fields (sql/224), by field id
  paid?: { account: string | null; reference: string } | null;   // paid on the spot: received into / paid from (sql/227)
  project_id?: string | null;                             // the project (sql/245)
  // the invoice form (sql/250)
  subject: string; salesperson_id: string | null; terms: string; due_date: string; order_number: string; notes: string; terms_text: string;
  disc: string; discPct: boolean;
  cheque?: ChequeForm | null;                             // 263_a: paid or received by cheque
  number_manual?: string; continue_series?: boolean;      // 263_c: the number typed by hand
  adjustment?: { ledger_id: string | null; amount: string; label: string } | null;   // 263_c: after tax, no GST
}
export interface ChequeForm { number: string; payee: string; drawn_on: string; ac_payee: boolean }
// what's wrong with the adjustment line, or null
export function adjustmentProblem(a: Form["adjustment"]): string | null {
  if (!a || !a.amount.trim()) return null;
  const n = Number(a.amount.replace(/,/g, ""));
  if (!Number.isFinite(n) || n === 0 || Math.round(n * 100) !== n * 100) return "The adjustment is an amount in rupees and paise, more or less than zero (e.g. -0.40 or 150).";
  if (!a.ledger_id) return "Choose the account the adjustment goes to.";
  return null;
}

let seq = 1;
export const newLine = (side: "Dr" | "Cr" = "Dr"): FormLine => ({ key: seq++, ledger_id: null, amount: "", side, narration: "", hsn: "", rate: "",
  item_name: "", service_id: null, qty: "", price: "", unit: "", disc: "", discPct: true });
// a new row of an invoice-type form: quantity 1, as other cloud books start it
export const newRow = (side: "Dr" | "Cr"): FormLine => ({ ...newLine(side), qty: "1" });

export function gstAvailable(ref: VoucherReference | null) {
  return !!ref?.gst && ref.gst.registrations.length > 0;
}

export function emptyForm(ref: VoucherReference, typeId: string, date?: string): Form {
  const t = ref.types.find((x) => x.id === typeId)!;
  const base = t.base_type;
  return {
    type_id: typeId, base, date: date ?? ref.today, narration: "", reference_no: "", reference_date: "",
    account: null, party: null, lines: [isInvoice(base) ? newRow(lineSide(base) ?? "Dr") : newLine(base === "journal" ? "Dr" : lineSide(base) ?? "Dr")],
    gstOn: isInvoice(base) && gstAvailable(ref), registration_id: ref.gst?.default_registration ?? ref.gst?.registrations[0]?.id ?? null,
    place_of_supply: null, reverse_charge: false, original_voucher: null, note_unlinked: false, tds: [], suffered: [],
    itemsMode: false, warehouse: null,
    subject: "", salesperson_id: null, terms: base === "sales" || base === "purchase" ? "due_on_receipt" : "", due_date: base === "sales" || base === "purchase" ? (date ?? ref.today) : "",
    order_number: "", notes: "", terms_text: "", disc: "", discPct: true,
  };
}

export const emptyItem = (): ItemPart => ({ variant_id: null, qty: "", uom_id: null, rate: "", batch: "", expiry: "" });
const r2 = (n: number) => Math.round(n * 100) / 100;
// a row of the invoice form: quantity × rate, before its discount
export const priced = (l: FormLine) => l.price.trim() !== "" && Number(l.qty) > 0 && parseAmount(l.price) != null && !Number.isNaN(parseAmount(l.price));
export function lineGross(l: FormLine): number | null {
  if (priced(l)) return r2(Number(l.qty) * parseAmount(l.price)!);
  return null;
}
export function lineDiscount(l: FormLine): number {
  const g = lineGross(l), d = parseAmount(l.disc);
  if (g == null || d == null || Number.isNaN(d) || d <= 0) return 0;
  return Math.min(g, r2(l.discPct ? (g * d) / 100 : d));
}
// a line's amount: on the invoice form quantity × rate less discount; an item line quantity × rate; else what was typed
export function lineAmount(l: FormLine): number | null {
  const g = lineGross(l);
  if (g != null) return r2(g - lineDiscount(l));
  if (l.item?.variant_id && l.item.qty && l.item.rate) {
    const q = Number(l.item.qty), r = parseAmount(l.item.rate);
    if (q > 0 && r != null && !Number.isNaN(r)) return Math.round(q * r * 100) / 100;
  }
  const n = parseAmount(l.amount);
  return n == null || Number.isNaN(n) ? null : n;
}
const itemLine = (f: Form, l: FormLine) => f.itemsMode && isInvoice(f.base) && !!l.item?.variant_id;
const lineOk = (f: Form, l: FormLine) => !!lineAmount(l) && (!!l.ledger_id || (itemLine(f, l) && (f.base === "purchase" || f.base === "debit_note")));

const num = (s: string) => { const n = parseAmount(s); return n == null || Number.isNaN(n) ? null : n; };

// what the lines add up to on the type's own side (journal: Dr − Cr)
export function linesTotal(f: Form): number {
  const own = lineSide(f.base) ?? "Dr";
  return Math.round(f.lines.reduce((t, l) => t + (lineAmount(l) ?? 0) * (l.side === own || !lineSide(f.base) && l.side === "Dr" ? 1 : -1), 0) * 100) / 100;
}

// the invoice's own discount, in rupees
export function formDiscount(f: Form, subTotal: number): number {
  const d = parseAmount(f.disc);
  if (!isInvoice(f.base) || d == null || Number.isNaN(d) || d <= 0 || subTotal <= 0) return 0;
  return Math.min(subTotal, r2(f.discPct ? (subTotal * d) / 100 : d));
}
// the invoice's discount spread over its lines in proportion (the last line takes what rounding leaves)
export function spread(amounts: number[], discount: number): number[] {
  const total = amounts.reduce((t, a) => t + a, 0);
  if (!discount || total <= 0) return amounts;
  let left = discount;
  return amounts.map((a, i) => {
    const cut = i === amounts.length - 1 ? left : r2((discount * a) / total);
    left = r2(left - cut);
    return r2(a - cut);
  });
}

export function toEntry(f: Form): Entry {
  const own = lineSide(f.base);
  const kept = f.lines.filter((l) => lineOk(f, l));
  const nets = kept.map((l) => lineAmount(l) ?? 0);
  const disc = formDiscount(f, nets.reduce((t, a) => t + a, 0));
  const after = spread(nets, disc);
  const lines = kept.map((l, i) => {
    const out: Entry["lines"][number] = { ledger_id: l.ledger_id, amount: after[i] };
    if (itemLine(f, l)) out.item = { variant_id: l.item!.variant_id!, qty: Number(priced(l) ? l.qty : l.item!.qty) || 0, uom_id: l.item!.uom_id, location_id: f.warehouse,
                                     batch: l.item!.batch || null, expiry: l.item!.expiry || null };
    if (priced(l)) {
      out.qty = Number(l.qty); out.rate = parseAmount(l.price)!;
      if (l.unit.trim()) out.unit = l.unit.trim();
      const d = parseAmount(l.disc);
      if (d && d > 0) out.discount = { value: d, percent: l.discPct };
      if (disc) out.pre = nets[i];
    }
    if (l.item_name.trim()) out.item_name = l.item_name.trim();
    if (l.service_id) out.service_id = l.service_id;
    if (!own || l.side !== own) out.side = l.side;
    if (l.narration.trim()) out.narration = l.narration.trim();
    if (l.bills?.length) out.bills = l.bills;
    if (l.billable?.customer_id) out.billable = l.billable;
    if (l.person_id) out.person_id = l.person_id;
    if (l.billable_ids?.length) out.billable_ids = l.billable_ids;
    const shares = (l.cost ?? []).filter((c) => c.cost_centre_id && parseAmount(c.value));
    if (shares.length) out.cost = shares.map((c) => (l.costPct ? { cost_centre_id: c.cost_centre_id!, percent: parseAmount(c.value)! }
                                                              : { cost_centre_id: c.cost_centre_id!, amount: parseAmount(c.value)! }));
    if (f.gstOn && (l.hsn.trim() || l.rate.trim()))
      out.gst = { hsn_sac: l.hsn.trim() || null, rate: l.rate.trim() === "" ? 0 : Number(l.rate), taxability: l.taxability || "taxable", itc: "eligible",
                  ...(l.item_name.trim() ? { description: l.item_name.trim().slice(0, 300) } : {}) };
    return out;
  });
  const e: Entry = { type_id: f.type_id, date: f.date, lines };
  // 263_c: the adjustment, after tax and outside GST: one more line to its account (the other side when it is negative)
  const adj = f.adjustment && isInvoice(f.base) && !adjustmentProblem(f.adjustment) ? Number(f.adjustment.amount.replace(/,/g, "")) : 0;
  if (adj && f.adjustment?.ledger_id && own) {
    lines.push({ ledger_id: f.adjustment.ledger_id, amount: Math.abs(adj), narration: f.adjustment.label.trim() || "Adjustment", adjustment: true,
                 ...(adj < 0 ? { side: own === "Dr" ? "Cr" : "Dr" } : {}) });
  }
  if (hasAccount(f.base) && f.cheque && f.base !== "contra") {
    e.cheque = { ac_payee: f.cheque.ac_payee, ...(f.cheque.number.trim() ? { number: f.cheque.number.trim() } : {}),
                 ...(f.cheque.payee.trim() ? { payee: f.cheque.payee.trim() } : {}), ...(f.cheque.drawn_on.trim() ? { drawn_on: f.cheque.drawn_on.trim() } : {}) };
  }
  if (isInvoice(f.base) && f.number_manual?.trim()) { e.number_manual = f.number_manual.trim().toUpperCase(); if (f.continue_series) e.continue_series = true; }
  if (isInvoice(f.base)) {
    if (f.subject.trim()) e.subject = f.subject.trim();
    if (f.salesperson_id) e.salesperson_id = f.salesperson_id;
    if (f.terms) e.terms = f.terms;
    if (f.due_date && f.terms) e.due_date = f.due_date;
    if (f.order_number.trim()) e.order_number = f.order_number.trim();
    if (f.notes.trim()) e.notes = f.notes.trim();
    if (f.terms_text.trim()) e.terms_text = f.terms_text.trim();
    const d = parseAmount(f.disc);
    if (d && d > 0) e.discount = { value: d, percent: f.discPct };
  }
  const fields = Object.fromEntries(Object.entries(f.fields ?? {}).filter(([, v]) => v.trim()));
  if (Object.keys(fields).length) e.fields = fields;
  if (f.paid?.account && (f.base === "sales" || f.base === "purchase")) e.paid = { account_ledger_id: f.paid.account, ...(f.paid.reference.trim() ? { reference_no: f.paid.reference.trim() } : {}) };
  if (f.currency) { e.currency = f.currency; const r = num(f.fx_rate ?? ""); if (r) e.fx_rate = r; }
  if (f.id) e.id = f.id;
  if (f.order_id) e.order_id = f.order_id;
  if (f.project_id) e.project_id = f.project_id;
  // the e-way bill answer goes once it is complete (half-typed transport details would stop the preview)
  if (f.ewaybill && f.base === "sales" && !ewayProblem(null, f.ewaybill)) e.ewaybill = cleanEway(f.ewaybill);
  if (f.narration.trim()) e.narration = f.narration.trim();
  if (f.reference_no.trim()) e.reference_no = f.reference_no.trim();
  if (f.reference_date) e.reference_date = f.reference_date;
  if (hasAccount(f.base)) e.account_ledger_id = f.account;
  if (f.base === "receipt" && f.advance_gst?.rate) e.advance_gst = f.advance_gst;
  if ((f.base === "sales" || f.base === "purchase") && f.adjust_advances?.some((a) => a.amount > 0)) e.adjust_advances = f.adjust_advances.filter((a) => a.amount > 0);
  if (isInvoice(f.base)) {
    e.party_ledger_id = f.party;
    if (f.party_bills?.length) e.party_bills = f.party_bills;
    e.gst = f.gstOn ? {
      registration_id: f.registration_id, place_of_supply: f.place_of_supply, reverse_charge: f.reverse_charge,
      ...(f.base === "credit_note" || f.base === "debit_note" ? { original_voucher: f.original_voucher, note_unlinked: !f.original_voucher && f.note_unlinked } : {}),
      ...(f.base === "purchase" || f.base === "debit_note" ? { supplier_doc_no: f.reference_no.trim() || null, supplier_doc_date: f.reference_date || null } : {}),
      ...((f.base === "sales" || f.base === "credit_note") && f.eco_ledger ? { eco: { ledger_id: f.eco_ledger, section: f.eco_section ?? "52" } } : {}),
      ...((f.base === "sales" || f.base === "credit_note") && f.eco_for && !f.eco_ledger ? { eco_supply: { supplier_gstin: f.eco_for.gstin.trim().toUpperCase() || null } } : {}),
      ...((f.base === "sales" || f.base === "credit_note") && f.zero ? { supply_type: zeroSupplyType(f.zero), export: {
        igst_from_buyer: f.zero.with_tax ? f.zero.igst_from_buyer : null,
        ...(f.zero.kind === "export" && f.base === "sales" ? { shipping_bill_no: f.zero.sb_no.trim() || null, shipping_bill_date: f.zero.sb_date || null,
                                                               shipping_port: f.zero.sb_port.trim().toUpperCase() || null } : {}) } } : {}),
    } : null;
  }
  const tds = f.tds.filter((r) => r.party_ledger_id && r.provision_code && num(r.base));
  if (tds.length) e.tds = tds.map((r) => ({ party_ledger_id: r.party_ledger_id, provision_code: r.provision_code, base_amount: num(r.base)!,
                                            skip_reason: r.skip ? r.skip_reason.trim() || null : null, ...(r.gross && !r.skip ? { gross_up: true } : {}) }));
  const suf = f.suffered.filter((r) => r.party_ledger_id && num(r.base) && num(r.tax));
  if (suf.length) e.tds_suffered = suf.map((r) => ({ party_ledger_id: r.party_ledger_id, kind: r.kind, base_amount: num(r.base)!, tax: num(r.tax)! }));
  return e;
}

export function fromEntry(e: Entry, ref: VoucherReference): Form {
  const f = emptyForm(ref, e.type_id, e.date);
  const own = lineSide(f.base);
  const journal = !own || !!e.as_journal;
  return {
    ...f, id: e.id, base: journal && e.as_journal ? "journal" : f.base, narration: e.narration ?? "", reference_no: e.reference_no ?? "",
    reference_date: e.reference_date ?? "", account: e.account_ledger_id ?? null, party: e.party_ledger_id ?? null, party_bills: e.party_bills,
    advance_gst: e.advance_gst ?? null, adjust_advances: e.adjust_advances,
    subject: e.subject ?? "", salesperson_id: e.salesperson_id ?? null, terms: e.terms ?? "", due_date: e.due_date ?? "", order_number: e.order_number ?? "",
    notes: e.notes ?? "", terms_text: e.terms_text ?? "", disc: e.discount ? String(e.discount.value) : "", discPct: e.discount ? e.discount.percent : true,
    cheque: e.cheque ? { number: e.cheque.number ?? "", payee: e.cheque.payee ?? "", drawn_on: e.cheque.drawn_on ?? "", ac_payee: e.cheque.ac_payee ?? true } : null,
    number_manual: e.number_manual ?? "", continue_series: !!e.continue_series,
    adjustment: (() => { const a = e.lines.find((l) => l.adjustment); return a ? { ledger_id: a.ledger_id, amount: String((a.side && a.side !== own ? -1 : 1) * (a.amount ?? 0)), label: a.narration ?? "" } : null; })(),
    lines: e.lines.filter((l) => !l.adjustment).map((l) => ({ ...newLine(), billable: l.billable ?? null, billable_ids: l.billable_ids, ...rowOf(l, isInvoice(f.base) && !journal), ledger_id: l.ledger_id, amount: l.amount != null ? String(l.amount) : "",
      side: l.side ?? own ?? "Dr", narration: l.narration ?? "", person_id: l.person_id ?? null, hsn: l.gst?.hsn_sac ?? "", rate: l.gst?.rate != null ? String(l.gst.rate) : "", taxability: l.gst?.taxability ?? undefined, bills: l.bills,
      ...(l.cost?.length ? { costPct: l.cost[0].percent != null, cost: l.cost.map((c) => ({ cost_centre_id: c.cost_centre_id, value: String(c.percent ?? c.amount ?? "") })) } : {}),
      item: l.item ? { variant_id: l.item.variant_id, qty: String(l.item.qty), uom_id: l.item.uom_id ?? null,
                       rate: l.item.qty && l.amount ? String(Math.round((l.amount / l.item.qty) * 10000) / 10000) : "", batch: l.item.batch ?? "", expiry: l.item.expiry ?? "" } : undefined })),
    fields: e.fields ?? {},
    paid: e.paid ? { account: e.paid.account_ledger_id, reference: e.paid.reference_no ?? "" } : null,
    currency: e.currency ?? "", fx_rate: e.fx_rate != null ? String(e.fx_rate) : "",
    order_id: e.order_id ?? null, project_id: e.project_id ?? null, ewaybill: e.ewaybill ?? null, eco_ledger: e.gst?.eco?.ledger_id ?? null, eco_section: e.gst?.eco?.section ?? "52", eco_for: e.gst?.eco_supply ? { gstin: e.gst.eco_supply.supplier_gstin ?? "" } : null, zero: zeroOf(e.gst?.supply_type, e.gst?.export), itemsMode: e.lines.some((l) => !!l.item), warehouse: e.lines.find((l) => l.item)?.item?.location_id ?? null,
    gstOn: !!e.gst, registration_id: e.gst?.registration_id ?? f.registration_id, place_of_supply: e.gst?.place_of_supply ?? null,
    reverse_charge: !!e.gst?.reverse_charge, original_voucher: e.gst?.original_voucher ?? null, note_unlinked: !!e.gst?.note_unlinked,
    tds: (e.tds ?? []).map((r) => ({ party_ledger_id: r.party_ledger_id, provision_code: r.provision_code, base: String(r.base_amount), skip: !!r.skip_reason, skip_reason: r.skip_reason ?? "", gross: !!r.gross_up })),
    suffered: (e.tds_suffered ?? []).map((r) => ({ party_ledger_id: r.party_ledger_id, kind: r.kind, base: String(r.base_amount), tax: String(r.tax) })),
  };
}

// an entry line back on the invoice form: quantity and rate as typed; an older line (amount only) as 1 × its amount
function rowOf(l: Entry["lines"][number], form: boolean): Partial<FormLine> {
  const base = { item_name: l.item_name ?? l.gst?.description ?? "", service_id: l.service_id ?? null, unit: l.unit ?? "",
                 disc: l.discount ? String(l.discount.value) : "", discPct: l.discount ? l.discount.percent : true };
  if (l.qty != null && l.rate != null) return { ...base, qty: String(l.qty), price: String(l.rate) };
  if (!form || l.amount == null) return base;
  if (l.item?.qty) return { ...base, qty: String(l.item.qty), price: String(Math.round(((l.pre ?? l.amount) / l.item.qty) * 10000) / 10000) };
  return { ...base, qty: "1", price: String(l.pre ?? l.amount) };
}

// worth asking the server about: enough is filled in
export function ready(f: Form): boolean {
  const lines = f.lines.filter((l) => lineOk(f, l));
  if (!f.date || !lines.length) return false;
  if (isInvoice(f.base) && !f.party) return false;
  if (hasAccount(f.base) && !f.account) return false;
  if (f.base === "journal" && lines.length < 2) return false;
  return true;
}

// a party whose usual provision applies to this kind of voucher: TDS on bills / payments / journals; TCS on sales
export function tdsFor(base: BaseType, ledger: VLedger | undefined, ref: VoucherReference): { provision: string; kind: "tds" | "tcs" } | null {
  if (!ref.tds_ready || !ledger?.provision) return null;
  const p = ref.tds_provisions?.find((x) => x.code === ledger.provision);
  if (!p) return null;
  if (p.kind === "tds" && ["purchase", "payment", "journal"].includes(base)) return { provision: p.code, kind: "tds" };
  if (p.kind === "tcs" && ["sales", "receipt", "journal"].includes(base)) return { provision: p.code, kind: "tcs" };
  return null;
}
